import { Label, LabelsByBoards } from '../../../../../../common/labels/labelTypes';

/**
 * Merge the existing and latest 'current labels' lists, while ensuring that only available labels are included and that
 * new labels are added. The only difference from using the latest list directly is that labels that are no longer
 * available due to being removed from the last element will still appear in the list.
 * The allLabels list is used to refresh label data (name/color) for all labels, including preserved ones.
 */
export const mergeCurrentLabels = (
    existingCurrentLabels: LabelsByBoards,
    latestCurrentLabels: LabelsByBoards,
    allLabels: Label[],
): LabelsByBoards => {
    const allLabelsById = new Map(allLabels.map((label) => [label.id, label]));

    const mergedLabelsByBoards = existingCurrentLabels
        .map(({ title, labels }) => ({
            title,
            labels: labels
                .filter((label) => allLabelsById.has(label.id))
                .map((label) => allLabelsById.get(label.id) || label),
        }))
        .filter(({ labels }) => labels.length > 0);

    const mergedLabelIds = new Set(mergedLabelsByBoards.flatMap(({ labels }) => labels.map((label) => label.id)));

    latestCurrentLabels.forEach(({ title, labels }) => {
        const labelsToAdd = labels.filter((label) => allLabelsById.has(label.id) && !mergedLabelIds.has(label.id));

        if (!labelsToAdd.length) return;

        labelsToAdd.forEach((label) => mergedLabelIds.add(label.id));

        const mergedGroup = mergedLabelsByBoards.find((group) => group.title === title);
        if (mergedGroup) {
            mergedGroup.labels = [...mergedGroup.labels, ...labelsToAdd];
            return;
        }

        mergedLabelsByBoards.push({ title, labels: labelsToAdd });
    });

    return mergedLabelsByBoards;
};
